import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Link,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ChapterSlideshow from './ChapterSlideshow';
import { internalProblemPathFromLeetcodeUrl, isInternalRoute } from '../utils/problemLinks';

// Copyable code block component
const CodeBlock = ({ code, language = 'python', title }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box sx={{ position: 'relative', my: 2 }}>
      {title && (
        <Typography sx={{ color: '#5168ff', fontWeight: 'bold', mb: 1, fontSize: '0.9rem' }}>
          {title}
        </Typography>
      )}
      <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
        <IconButton
          onClick={handleCopy}
          sx={{
            position: 'absolute',
            top: title ? 32 : 8,
            right: 8,
            zIndex: 1,
            bgcolor: 'rgba(255,255,255,0.1)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
          }}
          size="small"
        >
          {copied ? (
            <CheckIcon sx={{ fontSize: 16, color: '#15905f' }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 16, color: 'text.primary' }} />
          )}
        </IconButton>
      </Tooltip>
      <SyntaxHighlighter
        language={language}
        style={vscDarkPlus}
        customStyle={{
          borderRadius: 8,
          padding: '16px',
          fontSize: '14px',
          margin: 0,
        }}
      >
        {code}
      </SyntaxHighlighter>
    </Box>
  );
};

// Difficulty chip component
const DifficultyChip = ({ difficulty }) => {
  const colors = {
    Easy: { bg: 'rgba(21, 144, 95, 0.2)', color: '#15905f' },
    Medium: { bg: 'rgba(183, 121, 31, 0.2)', color: '#b7791f' },
    Hard: { bg: 'rgba(244, 67, 54, 0.2)', color: '#f44336' },
  };
  const { bg, color } = colors[difficulty] || colors.Medium;

  return (
    <Chip
      label={difficulty}
      size="small"
      sx={{ bgcolor: bg, color, fontWeight: 'bold', fontSize: '0.75rem' }}
    />
  );
};

// Render markdown-like text with bold and code
const RenderText = ({ text, sx = {} }) => {
  if (!text) return null;
  return (
    <Typography
      sx={{
        color: 'text.primary',
        lineHeight: 1.7,
        whiteSpace: 'pre-line',
        ...sx,
      }}
      dangerouslySetInnerHTML={{
        __html: text
          .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #5168ff">$1</strong>')
          .replace(/`([^`]+)`/g, '<code style="background: rgba(255,255,255,0.1); padding: 2px 6px; border-radius: 4px; color: #5168ff;">$1</code>'),
      }}
    />
  );
};

// Problem card component
const ProblemCard = ({ problem }) => {
  const [expanded, setExpanded] = useState(false);
  const practicePath = internalProblemPathFromLeetcodeUrl(problem.link, problem.name);
  const internalPracticePath = isInternalRoute(practicePath);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        bgcolor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '8px !important',
        mb: 1,
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: '0 0 8px 0' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: 'text.primary' }} />}
        sx={{ minHeight: 56 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Typography sx={{ color: '#5168ff', fontWeight: 'bold', minWidth: 60 }}>
            {problem.number}
          </Typography>
          <Typography sx={{ color: 'text.primary', flex: 1 }}>{problem.name}</Typography>
          {problem.difficulty && <DifficultyChip difficulty={problem.difficulty} />}
          {practicePath && (
            <Tooltip title="Practice in TopSWE">
              <IconButton
                component={internalPracticePath ? RouterLink : Link}
                to={internalPracticePath ? practicePath : undefined}
                href={internalPracticePath ? undefined : practicePath}
                target={internalPracticePath ? undefined : '_blank'}
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {/* Description */}
        {problem.description && (
          <RenderText text={problem.description} sx={{ mb: 2 }} />
        )}

        {/* Examples */}
        {(problem.examples || problem.example) && (
          <Box sx={{ mb: 2, pl: 2, borderLeft: '3px solid rgba(81, 104, 255, 0.3)' }}>
            {(problem.examples || [problem.example]).map((ex, i) => (
              <Typography key={i} sx={{ color: 'text.secondary', fontSize: '0.9rem', mb: 0.5 }}>
                Ex {i + 1}. {ex}
              </Typography>
            ))}
          </Box>
        )}

        {/* Explanation */}
        {problem.explanation && (
          <RenderText text={problem.explanation} sx={{ mb: 2, fontStyle: 'italic', color: 'text.primary' }} />
        )}

        {/* Quick Reference (template info) */}
        {(problem.template || problem.range || problem.check) && (
          <Box
            sx={{
              bgcolor: 'rgba(81, 104, 255, 0.1)',
              borderRadius: 2,
              p: 2,
              mb: 2,
            }}
          >
            <Typography sx={{ color: '#5168ff', fontWeight: 'bold', mb: 1, fontSize: '0.9rem' }}>
              Quick Reference
            </Typography>
            {problem.template && (
              <Typography sx={{ color: 'text.primary', fontSize: '0.85rem' }}>
                <strong>Template:</strong> {problem.template}
              </Typography>
            )}
            {problem.range && (
              <Typography sx={{ color: 'text.primary', fontSize: '0.85rem' }}>
                <strong>Range:</strong> <code style={{ color: '#5168ff' }}>{problem.range}</code>
              </Typography>
            )}
            {problem.check && (
              <Typography sx={{ color: 'text.primary', fontSize: '0.85rem' }}>
                <strong>Check:</strong> <code style={{ color: '#5168ff' }}>{problem.check}</code>
              </Typography>
            )}
          </Box>
        )}

        {/* Multiple solutions */}
        {problem.solutions && problem.solutions.map((sol, i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Typography sx={{ color: '#5168ff', fontWeight: 'bold', mb: 1 }}>
              {sol.title}
            </Typography>
            {(sol.template || sol.range || sol.check) && (
              <Box sx={{ bgcolor: 'rgba(81, 104, 255, 0.05)', borderRadius: 1, p: 1, mb: 1 }}>
                {sol.template && (
                  <Typography sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                    Template: {sol.template} | Range: <code style={{ color: '#5168ff' }}>{sol.range}</code> | Check: <code style={{ color: '#5168ff' }}>{sol.check}</code>
                  </Typography>
                )}
              </Box>
            )}
            <CodeBlock code={sol.code} />
          </Box>
        ))}

        {/* Single code solution */}
        {problem.code && !problem.solutions && <CodeBlock code={problem.code} />}

        {/* Complexity */}
        {problem.complexity && (
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
              Time: <code style={{ color: '#5168ff' }}>{problem.complexity.time}</code>
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
              Space: <code style={{ color: '#5168ff' }}>{problem.complexity.space}</code>
            </Typography>
          </Box>
        )}

        {/* Note */}
        {problem.note && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 193, 7, 0.1)', borderRadius: 2, border: '1px solid rgba(255, 193, 7, 0.3)' }}>
            <RenderText text={problem.note} sx={{ fontSize: '0.9rem', color: 'text.primary' }} />
          </Box>
        )}

        {/* Subsections (for problems with multiple solutions/explanations) */}
        {problem.subsections && problem.subsections.map((sub, i) => (
          <Box key={i} sx={{ mt: 2 }}>
            <Typography sx={{ color: '#5168ff', fontWeight: 'bold', mb: 1 }}>
              {sub.title}
            </Typography>
            {sub.content && (
              <RenderText text={sub.content} sx={{ mb: 1, fontSize: '0.9rem' }} />
            )}
            {sub.code && (
              <CodeBlock code={sub.code} language={sub.language || 'python'} />
            )}
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

// Template card component
const TemplateCard = ({ template }) => {
  return (
    <Card
      sx={{
        bgcolor: 'rgba(81, 104, 255, 0.08)',
        border: '1px solid rgba(81, 104, 255, 0.3)',
        borderRadius: 2,
        mb: 2,
      }}
    >
      <CardContent>
        <Typography variant="h6" sx={{ color: '#5168ff', mb: 1 }}>
          {template.title}
        </Typography>
        {(template.description || template.content) && (
          <RenderText text={template.description || template.content} sx={{ mb: 2, fontSize: '0.95rem' }} />
        )}
        {template.code && <CodeBlock code={template.code} language={template.language || 'python'} />}
        {(template.notes || template.note) && (
          <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 1 }}>
            {template.notes || template.note}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// Key points with title and content
const KeyPointsSection = ({ keyPoints }) => {
  if (!keyPoints || keyPoints.length === 0) return null;

  // Check if keyPoints are objects with title/content or just strings
  const isObjectFormat = keyPoints[0] && typeof keyPoints[0] === 'object' && keyPoints[0].title;

  return (
    <Box
      sx={{
        bgcolor: 'rgba(183, 121, 31, 0.1)',
        border: '1px solid rgba(183, 121, 31, 0.3)',
        borderRadius: 2,
        p: 2,
        mt: 2,
      }}
    >
      <Typography sx={{ color: '#b7791f', fontWeight: 'bold', mb: 2 }}>
        Important Notes
      </Typography>
      {isObjectFormat ? (
        keyPoints.map((point, i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Typography sx={{ color: '#ffb74d', fontWeight: 'bold', fontSize: '0.95rem', mb: 0.5 }}>
              {point.title}
            </Typography>
            <RenderText text={point.content} sx={{ fontSize: '0.9rem', color: 'text.primary' }} />
          </Box>
        ))
      ) : (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {keyPoints.map((point, i) => (
            <li key={i}>
              <RenderText text={point} sx={{ fontSize: '0.9rem' }} />
            </li>
          ))}
        </ul>
      )}
    </Box>
  );
};

// Walkthrough component
const WalkthroughSection = ({ walkthrough }) => {
  if (!walkthrough) return null;

  return (
    <Box sx={{ bgcolor: 'rgba(21, 144, 95, 0.1)', borderRadius: 2, p: 2, mt: 2, border: '1px solid rgba(21, 144, 95, 0.3)' }}>
      <Typography sx={{ color: '#15905f', fontWeight: 'bold', mb: 1 }}>
        {walkthrough.title || 'Walkthrough'}
      </Typography>
      {walkthrough.example && (
        <Typography sx={{ color: 'text.primary', mb: 1, fontFamily: 'monospace' }}>
          {walkthrough.example}
        </Typography>
      )}
      {walkthrough.description && (
        <RenderText text={walkthrough.description} sx={{ mb: 1.5, fontSize: '0.9rem' }} />
      )}
      {walkthrough.steps && (
        <ol style={{ margin: 0, paddingLeft: 20 }}>
          {walkthrough.steps.map((step, i) => (
            <li key={i}>
              <Typography sx={{ color: 'text.primary', fontSize: '0.9rem', mb: 0.5 }}>
                {step}
              </Typography>
            </li>
          ))}
        </ol>
      )}
      {walkthrough.proof && (
        <RenderText text={walkthrough.proof} sx={{ mt: 1.5, fontSize: '0.9rem', color: 'text.secondary' }} />
      )}
    </Box>
  );
};

const DataTables = ({ tables }) => {
  if (!tables || tables.length === 0) return null;

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      {tables.map((table) => (
        <Box
          key={table.id || table.title}
          sx={{
            mb: 2.5,
            overflowX: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          {table.title && (
            <Typography sx={{ px: 2, py: 1.4, color: 'text.primary', fontWeight: 850, borderBottom: '1px solid', borderColor: 'divider' }}>
              {table.title}
            </Typography>
          )}
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
            {table.headers && (
              <Box component="thead">
                <Box component="tr">
                  {table.headers.map((header) => (
                    <Box
                      component="th"
                      key={header}
                      sx={{
                        px: 1.5,
                        py: 1.2,
                        textAlign: 'left',
                        color: 'text.secondary',
                        fontSize: '0.78rem',
                        fontWeight: 850,
                        bgcolor: 'action.hover',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {header}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            <Box component="tbody">
              {(table.rows || []).map((row, rowIndex) => (
                <Box component="tr" key={`${table.id || table.title}-${rowIndex}`}>
                  {row.map((cell, cellIndex) => (
                    <Box
                      component="td"
                      key={`${rowIndex}-${cellIndex}`}
                      sx={{
                        px: 1.5,
                        py: 1.2,
                        color: cellIndex === 0 ? 'text.primary' : 'text.secondary',
                        fontSize: '0.88rem',
                        fontWeight: cellIndex === 0 ? 800 : 600,
                        borderBottom: rowIndex < table.rows.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        verticalAlign: 'top',
                        whiteSpace: cellIndex === 0 ? 'nowrap' : 'normal',
                      }}
                    >
                      {cell}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const ExamplePanel = ({ example }) => {
  if (!example) return null;

  return (
    <Box sx={{ bgcolor: 'rgba(81, 104, 255, 0.08)', border: '1px solid rgba(81, 104, 255, 0.22)', borderRadius: 2, p: 2, mt: 2 }}>
      <Typography sx={{ color: '#5168ff', fontWeight: 850, mb: 1 }}>
        {example.title || 'Example'}
      </Typography>
      {example.input && (
        <Typography sx={{ color: 'text.primary', fontFamily: 'monospace', fontSize: '0.9rem', mb: 0.75 }}>
          {example.input}
        </Typography>
      )}
      {example.expected && (
        <Typography sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.9rem', mb: 1 }}>
          {example.expected}
        </Typography>
      )}
      {example.steps && (
        <ol style={{ margin: 0, paddingLeft: 20 }}>
          {example.steps.map((step, index) => (
            <li key={`${step}-${index}`}>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.55, mb: 0.5 }}>
                {step}
              </Typography>
            </li>
          ))}
        </ol>
      )}
    </Box>
  );
};

const NotePanel = ({ title = 'Note', children, tone = 'note' }) => {
  if (!children) return null;
  const styles = tone === 'visualization'
    ? { bg: 'rgba(47, 99, 255, 0.08)', border: 'rgba(47, 99, 255, 0.22)', color: '#2f63ff' }
    : { bg: 'rgba(183, 121, 31, 0.1)', border: 'rgba(183, 121, 31, 0.28)', color: '#b7791f' };

  return (
    <Box sx={{ bgcolor: styles.bg, border: '1px solid', borderColor: styles.border, borderRadius: 2, p: 2, mt: 2 }}>
      <Typography sx={{ color: styles.color, fontWeight: 850, mb: 1 }}>
        {title}
      </Typography>
      <RenderText text={children} sx={{ fontSize: '0.92rem' }} />
    </Box>
  );
};

// Main section component
const Section = ({ section, defaultExpanded = true }) => {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      sx={{
        bgcolor: 'transparent',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '& .MuiAccordionSummary-root': {
          bgcolor: 'rgba(255,255,255,0.05)',
          borderRadius: 2,
          mb: 1,
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#5168ff' }} />}>
        <Typography variant="h5" sx={{ color: '#5168ff', fontWeight: 'bold' }}>
          {section.title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {/* Text content */}
        {section.content && <RenderText text={section.content} sx={{ mb: 2 }} />}

        {/* Walkthrough */}
        {section.walkthrough && <WalkthroughSection walkthrough={section.walkthrough} />}

        {/* Structured example */}
        {section.example && <ExamplePanel example={section.example} />}

        {/* Data tables */}
        {section.tables && <DataTables tables={section.tables} />}

        {/* Visualization note */}
        {section.visualization && <NotePanel title="Visualization" tone="visualization">{section.visualization}</NotePanel>}

        {/* Section note */}
        {section.note && <NotePanel>{section.note}</NotePanel>}

        {/* Key insight */}
        {section.keyInsight && (
          <Box sx={{ bgcolor: 'rgba(156, 39, 176, 0.1)', borderRadius: 2, p: 2, mt: 2, border: '1px solid rgba(156, 39, 176, 0.3)' }}>
            <Typography sx={{ color: '#ce93d8', fontWeight: 'bold', mb: 1 }}>Key Insight</Typography>
            <RenderText text={section.keyInsight} sx={{ fontSize: '0.95rem' }} />
          </Box>
        )}

        {/* Examples list */}
        {section.examples && (
          <Box sx={{ mb: 2 }}>
            {section.examples.map((ex, i) => (
              <Box
                key={i}
                sx={{
                  py: 1.5,
                  borderBottom: i < section.examples.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                }}
              >
                <Typography sx={{ color: '#5168ff', fontWeight: 'bold', mb: 0.5 }}>
                  {ex.title}
                </Typography>
                <Typography sx={{ color: 'text.primary', fontSize: '0.9rem' }}>
                  {ex.description}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Template subsections */}
        {section.subsections && section.subsections.map((sub) => (
          <TemplateCard key={sub.id} template={sub} />
        ))}

        {/* Complexity info */}
        {section.complexity && (
          <Box sx={{ display: 'flex', gap: 3, mb: 2, mt: 2 }}>
            <Chip
              label={`Time: ${section.complexity.time}`}
              sx={{ bgcolor: 'rgba(21, 144, 95, 0.2)', color: '#15905f' }}
            />
            <Chip
              label={`Space: ${section.complexity.space}`}
              sx={{ bgcolor: 'rgba(33, 150, 243, 0.2)', color: '#2196f3' }}
            />
          </Box>
        )}

        {/* Key points */}
        {section.keyPoints && <KeyPointsSection keyPoints={section.keyPoints} />}

        {/* Intro text for problem sections */}
        {section.intro && <RenderText text={section.intro} sx={{ mb: 2 }} />}

        {/* Problems list */}
        {section.problems && section.problems.map((problem) => (
          <ProblemCard key={problem.id || problem.number} problem={problem} />
        ))}

        {/* Insight box */}
        {section.insight && (
          <Box
            sx={{
              bgcolor: 'rgba(156, 39, 176, 0.1)',
              border: '1px solid rgba(156, 39, 176, 0.3)',
              borderRadius: 2,
              p: 2,
              mt: 2,
            }}
          >
            <Typography sx={{ color: '#ce93d8', fontWeight: 'bold', mb: 1 }}>Deep Dive</Typography>
            <RenderText text={section.insight} sx={{ fontSize: '0.9rem' }} />
          </Box>
        )}

        {/* Standalone code block */}
        {section.code && !section.subsections && (
          <CodeBlock code={section.code} />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

// Single section content renderer (no accordion wrapper)
const SingleSectionContent = ({ section }) => {
  if (!section) return null;

  return (
    <Box>
      <Typography variant="h4" sx={{ color: 'text.primary', mb: 3, fontWeight: 'bold' }}>
        {section.title}
      </Typography>
      <Divider sx={{ borderColor: 'rgba(81, 104, 255, 0.3)', mb: 3 }} />

      {/* Text content */}
      {section.content && <RenderText text={section.content} sx={{ mb: 2 }} />}

      {/* Walkthrough */}
      {section.walkthrough && <WalkthroughSection walkthrough={section.walkthrough} />}

      {/* Structured example */}
      {section.example && <ExamplePanel example={section.example} />}

      {/* Data tables */}
      {section.tables && <DataTables tables={section.tables} />}

      {/* Visualization note */}
      {section.visualization && <NotePanel title="Visualization" tone="visualization">{section.visualization}</NotePanel>}

      {/* Section note */}
      {section.note && <NotePanel>{section.note}</NotePanel>}

      {/* Key insight */}
      {section.keyInsight && (
        <Box sx={{ bgcolor: 'rgba(156, 39, 176, 0.1)', borderRadius: 2, p: 2, mt: 2, border: '1px solid rgba(156, 39, 176, 0.3)' }}>
          <Typography sx={{ color: '#ce93d8', fontWeight: 'bold', mb: 1 }}>Key Insight</Typography>
          <RenderText text={section.keyInsight} sx={{ fontSize: '0.95rem' }} />
        </Box>
      )}

      {/* Examples list */}
      {section.examples && (
        <Box sx={{ mb: 2 }}>
          {section.examples.map((ex, i) => (
            <Box
              key={i}
              sx={{
                py: 1.5,
                borderBottom: i < section.examples.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
              }}
            >
              <Typography sx={{ color: '#5168ff', fontWeight: 'bold', mb: 0.5 }}>
                {ex.title}
              </Typography>
              <Typography sx={{ color: 'text.primary', fontSize: '0.9rem' }}>
                {ex.description}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Template subsections */}
      {section.subsections && section.subsections.map((sub) => (
        <TemplateCard key={sub.id} template={sub} />
      ))}

      {/* Complexity info */}
      {section.complexity && (
        <Box sx={{ display: 'flex', gap: 3, mb: 2, mt: 2 }}>
          <Chip
            label={`Time: ${section.complexity.time}`}
            sx={{ bgcolor: 'rgba(21, 144, 95, 0.2)', color: '#15905f' }}
          />
          <Chip
            label={`Space: ${section.complexity.space}`}
            sx={{ bgcolor: 'rgba(33, 150, 243, 0.2)', color: '#2196f3' }}
          />
        </Box>
      )}

      {/* Key points */}
      {section.keyPoints && <KeyPointsSection keyPoints={section.keyPoints} />}

      {/* Intro text for problem sections */}
      {section.intro && <RenderText text={section.intro} sx={{ mb: 2 }} />}

      {/* Problems list */}
      {section.problems && section.problems.map((problem) => (
        <ProblemCard key={problem.id || problem.number} problem={problem} />
      ))}

      {/* Insight box */}
      {section.insight && (
        <Box
          sx={{
            bgcolor: 'rgba(156, 39, 176, 0.1)',
            border: '1px solid rgba(156, 39, 176, 0.3)',
            borderRadius: 2,
            p: 2,
            mt: 2,
          }}
        >
          <Typography sx={{ color: '#ce93d8', fontWeight: 'bold', mb: 1 }}>Deep Dive</Typography>
          <RenderText text={section.insight} sx={{ fontSize: '0.9rem' }} />
        </Box>
      )}

      {/* Standalone code block */}
      {section.code && !section.subsections && (
        <CodeBlock code={section.code} />
      )}
    </Box>
  );
};

// Main chapter content component
const ChapterContent = ({ chapter, singleSection = false }) => {
  if (!chapter || !chapter.sections) {
    return <Typography sx={{ color: 'text.primary' }}>No content available</Typography>;
  }

  // Single section mode - render without accordion
  if (singleSection && chapter.sections.length === 1) {
    return <SingleSectionContent section={chapter.sections[0]} />;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ color: 'text.primary', mb: 3, fontWeight: 'bold' }}>
        {chapter.title}
      </Typography>
      <Divider sx={{ borderColor: 'rgba(81, 104, 255, 0.3)', mb: 3 }} />
      <ChapterSlideshow chapter={chapter} />
      {chapter.sections.map((section, index) => (
        <Section
          key={section.id}
          section={section}
          defaultExpanded={index < 5} // First 5 sections expanded by default
        />
      ))}
    </Box>
  );
};

export default ChapterContent;
